export function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return "--:--";
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes)}:${String(seconds).padStart(2, "0")}`;
}

export function formatClock(iso: string): string {
  const value = Date.parse(iso);
  if (Number.isNaN(value)) return "";
  return new Date(value).toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatRelative(iso: string, now: number): string {
  const value = Date.parse(iso);
  if (Number.isNaN(value)) return "";
  const seconds = Math.round((now - value) / 1000);
  if (seconds < 45) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${String(minutes)} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${String(hours)} hr ago`;
  return formatClock(iso);
}

/** A cue point, which is a place in a track rather than a length of one. */
export function formatCuePoint(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return "0:00.000";
  const totalSeconds = Math.floor(ms / 1000);
  return `${String(Math.floor(totalSeconds / 60))}:${String(
    totalSeconds % 60,
  ).padStart(2, "0")}.${String(Math.floor(ms % 1000)).padStart(3, "0")}`;
}

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "kB", "MB", "GB"];
  const order = Math.min(
    units.length - 1,
    Math.floor(Math.log10(bytes) / 3) || 0,
  );
  const value = bytes / 1000 ** order;
  return `${value >= 100 || order === 0 ? value.toFixed(0) : value.toFixed(1)} ${units[order] ?? "B"}`;
}

/** A date without a time, for the things that are dated rather than timed. */
export function formatDate(iso: string): string {
  const value = Date.parse(iso);
  if (Number.isNaN(value)) return "";
  return new Date(value).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/** The delivery a listener actually hears, said the way a listener would say it. */
export function formatDeliveryFormat(delivery: {
  codec: string;
  bitrateKbps: number;
  channels: number;
}): string {
  const channels = delivery.channels === 1 ? "mono" : "stereo";
  return `${delivery.codec.toUpperCase()} · ${String(delivery.bitrateKbps)} kbps · ${channels}`;
}
